import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/AddOutlined';
import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { apiClient } from '@/api/axiosClient';
import PageHeader from '@/components/common/PageHeader';
import DataTable from '@/components/common/DataTable';
import type { DataTableColumn } from '@/components/common/DataTable';
import SearchableSelect from '@/components/common/SearchableSelect';
import StatusChip from '@/components/common/StatusChip';
import { GridContainer, GridItem } from '@/components/common/ResponsiveGrid';
import { IfPermitted } from '@/components/auth/RequirePermission';
import { useDateFormat } from '@/hooks/useDateFormat';
import { useProcessConfiguration } from '@/hooks/useProcessConfiguration';
import { PERMISSIONS } from '@/types/auth';
import type { WarehouseDto } from '@/types/organization';
import type { ClientDto, MaterialDto, PagedResult, SupplierDto } from '@/types/materials';
import type { BomDto } from '@/types/manufacturing';
import type {
  CreateJobWorkOrderRequest,
  JobWorkOrderDto,
  JobWorkOrderLineDto,
  JobWorkOrderLineInput,
  UpdateJobWorkOrderRequest,
} from '@/types/jobWork';

type SourceMode = 'Bom' | 'Manual';

const EMPTY_LINE: JobWorkOrderLineInput = { materialId: '', requiredQty: 0 };

export default function JobWorkOrdersPage() {
  const { t } = useTranslation('jobWork');
  const { formatDate } = useDateFormat();
  const queryClient = useQueryClient();
  const { data: config } = useProcessConfiguration();

  const { data: orders = [], isLoading } = useQuery({
    queryKey: ['job-work-orders'],
    queryFn: async () => (await apiClient.get<JobWorkOrderDto[]>('/job-work-orders')).data,
  });
  const { data: suppliers = [] } = useQuery({
    queryKey: ['suppliers'],
    queryFn: async () => (await apiClient.get<SupplierDto[]>('/suppliers')).data,
  });
  const { data: warehouses = [] } = useQuery({
    queryKey: ['warehouses'],
    queryFn: async () => (await apiClient.get<WarehouseDto[]>('/warehouses')).data,
  });
  const { data: materialsPage } = useQuery({
    queryKey: ['materials', 'all-for-picker'],
    queryFn: async () => (await apiClient.get<PagedResult<MaterialDto>>('/materials', { params: { page: 1, pageSize: 200 } })).data,
  });
  const materials = materialsPage?.items ?? [];
  const { data: clients = [] } = useQuery({
    queryKey: ['clients'],
    queryFn: async () => (await apiClient.get<ClientDto[]>('/clients')).data,
    enabled: config?.thirdPartyLogisticsEnabled ?? false,
  });
  // BOM mode sends whatever the output material's Active BOM explodes to, so the output picker must
  // only offer materials that actually HAVE one — i.e. BOM header materials (FG/SFG by definition,
  // since BomService only accepts those as headers). Without this the operator could pick any
  // material and only discover at Save that the server has no BOM to explode.
  const { data: boms = [] } = useQuery({
    queryKey: ['boms'],
    queryFn: async () => (await apiClient.get<BomDto[]>('/boms')).data,
    enabled: config?.jobWorkBomEnabled ?? true,
  });
  const activeBoms = useMemo(() => boms.filter((b) => b.status === 'Active'), [boms]);

  // Only suppliers explicitly marked as job-work vendors — the same rule the backend re-checks.
  const jobWorkVendors = suppliers.filter((s) => s.isJobWorkVendor);

  // Which creation modes this company allows. If a company somehow has both switched off, fall back
  // to Manual so the dialog never renders with no usable mode at all (same guard as Production Plan).
  const bomEnabled = config?.jobWorkBomEnabled ?? true;
  const manualEnabled = config?.jobWorkManualEnabled ?? true;
  // Memoized on the two flags rather than rebuilt each render — the mode-sync effect below depends
  // on this array's identity.
  const effectiveModes = useMemo<SourceMode[]>(() => {
    const modes: SourceMode[] = [...(bomEnabled ? (['Bom'] as SourceMode[]) : []), ...(manualEnabled ? (['Manual'] as SourceMode[]) : [])];
    return modes.length > 0 ? modes : (['Manual'] as SourceMode[]);
  }, [bomEnabled, manualEnabled]);

  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<JobWorkOrderDto | null>(null);
  const [mode, setMode] = useState<SourceMode>('Bom');
  const [supplierId, setSupplierId] = useState('');
  const [warehouseId, setWarehouseId] = useState('');
  const [outputMaterialId, setOutputMaterialId] = useState('');
  const [plannedQty, setPlannedQty] = useState(0);
  const [clientId, setClientId] = useState('');
  const [expectedReturnDate, setExpectedReturnDate] = useState('');
  const [remarks, setRemarks] = useState('');
  const [lines, setLines] = useState<JobWorkOrderLineInput[]>([{ ...EMPTY_LINE }]);

  // Keeps the picked mode inside whatever Process Configuration currently allows. Needed because
  // `config` resolves asynchronously — the dialog can open while bomEnabled/manualEnabled are still
  // on their optimistic defaults — and because openEdit restores an old order's original mode, which
  // may since have been switched off for the company. Without this the form can sit on a mode the
  // server will reject ("BOM-based sending is switched off for this company").
  useEffect(() => {
    if (!effectiveModes.includes(mode)) {
      setMode(effectiveModes[0]);
    }
  }, [effectiveModes, mode]);

  const selectedBom = useMemo(() => activeBoms.find((b) => b.materialId === outputMaterialId), [activeBoms, outputMaterialId]);

  // A material chosen in Manual mode (any material) may have no Active BOM, so it can't survive a
  // switch to BOM mode — clear it rather than leaving a selection the picker can't even display.
  // Only runs once the BOM list has actually loaded, so it can't wipe a valid pick mid-fetch.
  useEffect(() => {
    if (mode === 'Bom' && outputMaterialId && boms.length > 0 && !activeBoms.some((b) => b.materialId === outputMaterialId)) {
      setOutputMaterialId('');
    }
  }, [mode, outputMaterialId, boms.length, activeBoms]);

  const resetForm = () => {
    setEditingId(null);
    setMode(effectiveModes[0]);
    setSupplierId('');
    setWarehouseId('');
    setOutputMaterialId('');
    setPlannedQty(0);
    setClientId('');
    setExpectedReturnDate('');
    setRemarks('');
    setLines([{ ...EMPTY_LINE }]);
  };

  const openEdit = (r: JobWorkOrderDto) => {
    setEditingId(r.id);
    setMode(r.bomId ? 'Bom' : 'Manual');
    setSupplierId(r.supplierId);
    setWarehouseId(r.warehouseId);
    setOutputMaterialId(r.outputMaterialId);
    setPlannedQty(r.plannedQty);
    setClientId(r.clientId ?? '');
    setExpectedReturnDate(r.expectedReturnDate ? r.expectedReturnDate.slice(0, 10) : '');
    setRemarks(r.remarks ?? '');
    setLines(r.lines.map((l) => ({ materialId: l.materialId, requiredQty: l.requiredQty, remarks: l.remarks })));
    setOpen(true);
  };

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['job-work-orders'] });

  const saveMutation = useMutation({
    mutationFn: () => {
      const cleanLines = lines.filter((l) => l.materialId && l.requiredQty > 0);
      const payload: CreateJobWorkOrderRequest = {
        supplierId,
        warehouseId,
        outputMaterialId,
        plannedQty,
        expectedReturnDate: expectedReturnDate || null,
        remarks: remarks || null,
        // BOM mode deliberately sends no lines — the server explodes the output material's Active
        // BOM instead. Manual mode sends the operator's own list.
        lines: mode === 'Manual' ? cleanLines : null,
        clientId: config?.thirdPartyLogisticsEnabled ? clientId || null : null,
      };
      if (editingId) {
        return apiClient.put(`/job-work-orders/${editingId}`, payload as UpdateJobWorkOrderRequest);
      }
      return apiClient.post('/job-work-orders', payload);
    },
    onSuccess: () => {
      invalidate();
      setOpen(false);
    },
  });

  const releaseMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/job-work-orders/${id}/release`),
    onSuccess: invalidate,
  });
  const closeMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/job-work-orders/${id}/close`),
    onSuccess: invalidate,
  });

  const columns: DataTableColumn<JobWorkOrderDto>[] = [
    { field: 'orderNumber', headerName: t('orders.table.number') },
    { field: 'supplierName', headerName: t('orders.table.vendor'), filterable: true },
    { field: 'outputMaterialCode', headerName: t('orders.table.outputMaterial'), render: (r) => `${r.outputMaterialCode} — ${r.outputMaterialName}` },
    { field: 'plannedQty', headerName: t('orders.table.plannedQty') },
    { field: 'warehouseName', headerName: t('orders.table.warehouse'), filterable: true },
    { field: 'orderDate', headerName: t('orders.table.date'), render: (r) => formatDate(r.orderDate) },
    { field: 'lines', headerName: t('orders.table.lines'), render: (r) => r.lines.length },
    {
      // The sent/pending balance the whole module is judged on — derived server-side, never stored.
      field: 'balance',
      headerName: t('orders.table.sentPending'),
      render: (r) => `${r.reconciliation.issuedQty} / ${r.reconciliation.requiredQty}`,
    },
    {
      // The returned side: what's physically come back against the output qty expected. Rejected
      // quantity is called out separately since it never becomes usable stock.
      field: 'returned',
      headerName: t('orders.table.receivedExpected'),
      render: (r) => (
        <Stack direction="row" spacing={0.75} alignItems="center">
          <span>{`${r.reconciliation.receivedQty} / ${r.reconciliation.plannedQty}`}</span>
          {r.reconciliation.rejectedQty > 0 && (
            <Chip size="small" color="error" variant="outlined" label={t('orders.table.rejectedChip', { qty: r.reconciliation.rejectedQty })} />
          )}
        </Stack>
      ),
    },
    {
      field: 'status',
      headerName: t('orders.table.status'),
      render: (r) => <StatusChip status={r.isClosed ? 'Closed' : r.status} />,
      filterable: true,
    },
    ...(config?.thirdPartyLogisticsEnabled
      ? [{ field: 'clientName', headerName: t('orders.table.client'), render: (r: JobWorkOrderDto) => r.clientName ?? '—', filterable: true } as DataTableColumn<JobWorkOrderDto>]
      : []),
    {
      field: 'actions',
      headerName: '',
      render: (r) => (
        <Stack direction="row" spacing={0.5}>
          <Button size="small" onClick={() => setViewing(r)}>{t('orders.view')}</Button>
          <IfPermitted permission={PERMISSIONS.JOB_WORK_MANAGE}>
            {r.status === 'Draft' && !r.isClosed && (
              <>
                <Button size="small" onClick={() => openEdit(r)}>{t('orders.edit')}</Button>
                <Button size="small" disabled={releaseMutation.isPending} onClick={() => releaseMutation.mutate(r.id)}>
                  {t('orders.release')}
                </Button>
              </>
            )}
            {!r.isClosed && r.status !== 'Draft' && (
              <Button size="small" color="warning" disabled={closeMutation.isPending} onClick={() => closeMutation.mutate(r.id)}>
                {t('orders.close')}
              </Button>
            )}
          </IfPermitted>
        </Stack>
      ),
    },
  ];

  const lineColumns: DataTableColumn<JobWorkOrderLineDto>[] = [
    { field: 'materialCode', headerName: t('orders.lineTable.material'), render: (l) => `${l.materialCode} — ${l.materialName}` },
    { field: 'requiredQty', headerName: t('orders.lineTable.requiredQty') },
    { field: 'issuedQty', headerName: t('orders.lineTable.issuedQty') },
    { field: 'pending', headerName: t('orders.lineTable.pendingQty'), render: (l) => l.requiredQty - l.issuedQty },
  ];

  return (
    <>
      <PageHeader
        title={t('orders.title')}
        subtitle={t('orders.subtitle')}
        actions={
          <IfPermitted permission={PERMISSIONS.JOB_WORK_MANAGE}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => {
                resetForm();
                setOpen(true);
              }}
            >
              {t('orders.add')}
            </Button>
          </IfPermitted>
        }
      />

      <DataTable columns={columns} rows={orders} loading={isLoading} searchable getRowId={(r) => r.id} exportFileName="job-work-orders" />

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? t('orders.edit') : t('orders.add')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Always rendered, never hidden — when Process Configuration allows only one of the two
                sending modes, this shows that single mode read-only so the operator can see which
                type is in force (and why there's no choice), instead of the field silently vanishing. */}
            <TextField
              select
              label={t('orders.form.mode')}
              // Falls back rather than rendering `mode` raw: the sync effect below corrects an
              // out-of-config mode one tick after config resolves, and without this the select
              // would briefly hold a value with no matching option in between.
              value={effectiveModes.includes(mode) ? mode : effectiveModes[0]}
              onChange={(e) => setMode(e.target.value as SourceMode)}
              fullWidth
              disabled={effectiveModes.length === 1}
              helperText={effectiveModes.length === 1 ? t('orders.form.modeLockedHint', { mode: t(`orders.modes.${effectiveModes[0]}`) }) : undefined}
            >
              {effectiveModes.map((m) => (
                <MenuItem key={m} value={m}>
                  {t(`orders.modes.${m}`)}
                </MenuItem>
              ))}
            </TextField>

            <SearchableSelect
              label={t('orders.form.vendor')} value={supplierId} onChange={setSupplierId} fullWidth
              helperText={jobWorkVendors.length === 0 ? t('orders.form.noVendorsHint') : undefined}
              options={jobWorkVendors.map((s) => ({ value: s.id, label: s.name }))}
            />
            <SearchableSelect
              label={t('orders.form.warehouse')} value={warehouseId} onChange={setWarehouseId} fullWidth
              options={warehouses.map((w) => ({ value: w.id, label: w.name }))}
            />
            {/* BOM mode: only BOM header materials (each has an Active BOM, so each is an FG/SFG),
                labelled with their BOM number. Manual mode: any material, since the operator picks
                the inputs themselves and no BOM is consulted. */}
            <SearchableSelect
              label={t('orders.form.outputMaterial')} value={outputMaterialId} onChange={setOutputMaterialId} fullWidth
              helperText={
                mode === 'Bom'
                  ? activeBoms.length === 0
                    ? t('orders.form.noActiveBomsHint')
                    : t('orders.form.outputMaterialBomHint')
                  : undefined
              }
              options={
                mode === 'Bom'
                  ? activeBoms.map((b) => ({ value: b.materialId, label: `${b.materialCode} — ${b.materialName} (${b.bomNumber})` }))
                  : materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))
              }
            />

            {/* Makes the "child items get picked up from the BOM header" rule visible before Save —
                the same explosion JobWorkOrderService.BuildLinesAsync performs server-side, scaled
                by planned qty against the BOM's own BaseQty. */}
            {mode === 'Bom' && selectedBom && (
              <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                  {t('orders.form.bomChildrenTitle', { bom: selectedBom.bomNumber })}
                </Typography>
                <Stack spacing={0.5} sx={{ mt: 1 }}>
                  {selectedBom.lines.map((l) => (
                    <Stack key={l.id} direction="row" justifyContent="space-between" alignItems="baseline">
                      <Typography variant="body2" color="text.secondary">
                        {`${l.materialCode} — ${l.materialName}`}
                      </Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {plannedQty > 0 ? Math.round(((l.qty * plannedQty) / selectedBom.baseQty) * 1000) / 1000 : l.qty}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              </Box>
            )}
            <TextField
              type="number" label={t('orders.form.plannedQty')} value={plannedQty} fullWidth
              onChange={(e) => setPlannedQty(Number(e.target.value))}
            />
            {config?.thirdPartyLogisticsEnabled && (
              <SearchableSelect
                label={t('orders.form.client')} value={clientId} onChange={setClientId} fullWidth
                options={clients.map((c) => ({ value: c.id, label: c.name }))}
              />
            )}
            <TextField
              type="date" label={t('orders.form.expectedReturnDate')} value={expectedReturnDate} fullWidth
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setExpectedReturnDate(e.target.value)}
            />
            <TextField label={t('orders.form.remarks')} value={remarks} onChange={(e) => setRemarks(e.target.value)} fullWidth multiline minRows={2} />

            {mode === 'Manual' && (
              <>
                <Divider />
                <Typography variant="subtitle2">{t('orders.form.lines')}</Typography>
                {lines.map((line, idx) => (
                  <Stack key={idx} direction="row" spacing={1} alignItems="center">
                    <SearchableSelect
                      label={t('orders.form.material')} value={line.materialId} fullWidth size="small"
                      onChange={(value) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, materialId: value } : l)))}
                      options={materials.map((m) => ({ value: m.id, label: `${m.code} — ${m.name}` }))}
                    />
                    <TextField
                      type="number" label={t('orders.form.qty')} value={line.requiredQty} size="small" sx={{ minWidth: 120 }}
                      onChange={(e) => setLines((prev) => prev.map((l, i) => (i === idx ? { ...l, requiredQty: Number(e.target.value) } : l)))}
                    />
                    <IconButton size="small" onClick={() => setLines((prev) => prev.filter((_, i) => i !== idx))} disabled={lines.length <= 1}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                ))}
                <Button size="small" startIcon={<AddIcon />} onClick={() => setLines((prev) => [...prev, { ...EMPTY_LINE }])} sx={{ alignSelf: 'flex-start' }}>
                  {t('orders.form.addLine')}
                </Button>
              </>
            )}

            {mode === 'Bom' && (
              <Typography variant="body2" color="text.secondary">
                {t('orders.form.bomModeHint')}
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setOpen(false)}>{t('orders.cancel')}</Button>
          <Button
            variant="contained"
            disabled={!supplierId || !warehouseId || !outputMaterialId || plannedQty <= 0 || saveMutation.isPending}
            onClick={() => saveMutation.mutate()}
          >
            {t('orders.save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reconciliation view — a plain LineItemsDialog can't carry the sent-vs-returned panel above
          the line table, so this screen builds its own shell around the same DataTable. */}
      <Dialog open={Boolean(viewing)} onClose={() => setViewing(null)} maxWidth="md" fullWidth>
        {viewing && (
          <>
            <DialogTitle>
              {viewing.orderNumber}
              <Typography variant="body2" color="text.secondary">
                {`${viewing.supplierName} — ${viewing.outputMaterialCode} ${viewing.outputMaterialName}`}
              </Typography>
            </DialogTitle>
            <DialogContent>
              <Typography variant="subtitle2" sx={{ mb: 1.5 }}>
                {t('orders.reconciliation.title')}
              </Typography>

              <GridContainer columns={12} spacing={2}>
                <GridItem xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        {t('orders.reconciliation.sentSide')}
                      </Typography>
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        <ReconRow label={t('orders.reconciliation.toSend')} value={viewing.reconciliation.requiredQty} />
                        <ReconRow label={t('orders.reconciliation.sent')} value={viewing.reconciliation.issuedQty} />
                        <ReconRow
                          label={t('orders.reconciliation.pendingToSend')}
                          value={viewing.reconciliation.pendingToSendQty}
                          highlight={viewing.reconciliation.pendingToSendQty > 0 ? 'warning.main' : undefined}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </GridItem>

                <GridItem xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase' }}>
                        {t('orders.reconciliation.returnedSide')}
                      </Typography>
                      <Stack spacing={0.5} sx={{ mt: 1 }}>
                        <ReconRow label={t('orders.reconciliation.expected')} value={viewing.reconciliation.plannedQty} />
                        <ReconRow label={t('orders.reconciliation.received')} value={viewing.reconciliation.receivedQty} />
                        <ReconRow label={t('orders.reconciliation.accepted')} value={viewing.reconciliation.acceptedQty} />
                        <ReconRow
                          label={t('orders.reconciliation.rejected')}
                          value={viewing.reconciliation.rejectedQty}
                          highlight={viewing.reconciliation.rejectedQty > 0 ? 'error.main' : undefined}
                        />
                        <ReconRow
                          label={t('orders.reconciliation.pendingToReceive')}
                          value={viewing.reconciliation.pendingToReceiveQty}
                          highlight={viewing.reconciliation.pendingToReceiveQty > 0 ? 'warning.main' : undefined}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </GridItem>
              </GridContainer>

              <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1.5 }}>
                {t('orders.reconciliation.footnote')}
              </Typography>

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                {t('orders.reconciliation.linesTitle')}
              </Typography>
              <DataTable columns={lineColumns} rows={viewing.lines} getRowId={(l) => l.id} searchable={false} exportable={false} />
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2 }}>
              <Button onClick={() => setViewing(null)}>{t('orders.cancel')}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}

/** One label/value line inside a reconciliation card. */
function ReconRow({ label, value, highlight }: { label: string; value: number; highlight?: string }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, color: highlight }}>
        {value}
      </Typography>
    </Stack>
  );
}
